import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Brain, Loader2, Play, CheckCircle2, AlertTriangle, Clock, XCircle, ChevronRight, Zap,
} from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

const STATUS_META = {
  succeeded: { label: 'Done',      icon: CheckCircle2, cls: 'text-emerald-600 dark:text-emerald-400' },
  failed:    { label: 'Failed',    icon: AlertTriangle, cls: 'text-red-600 dark:text-red-400' },
  cancelled: { label: 'Stopped',   icon: XCircle,      cls: 'text-slate-500' },
  timeout:   { label: 'Timed out', icon: Clock,        cls: 'text-amber-600 dark:text-amber-400' },
  skipped:   { label: 'Skipped',   icon: ChevronRight, cls: 'text-slate-400' },
  running:   { label: 'Running…',  icon: Loader2,      cls: 'text-sky-600 dark:text-sky-400' },
  pending:   { label: 'Queued',    icon: Clock,        cls: 'text-slate-400' },
};

function AgentRow({ agent }) {
  const meta = STATUS_META[agent.status] || STATUS_META.pending;
  const Icon = meta.icon;
  const spinning = agent.status === 'running';
  return (
    <div className="flex items-start gap-3 rounded-md border border orka-border-subtle px-3 py-2 text-sm"
      data-testid={`dryrun-agent-${agent.source}`}>
      <Icon className={`w-4 h-4 mt-0.5 ${meta.cls} ${spinning ? 'animate-spin' : ''}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium uppercase text-xs tracking-wider">{agent.source}</span>
          <Badge variant="outline" className="text-[10px]">{meta.label}</Badge>
          {typeof agent.latency_ms === 'number' && (
            <span className="text-[10px] text-slate-500">{agent.latency_ms}ms</span>
          )}
          {agent.item_count > 0 && (
            <span className="text-[10px] text-slate-500">{agent.item_count} items</span>
          )}
        </div>
        {agent.error && (
          <div className="text-[11px] text-red-600 mt-0.5">
            <span className="font-mono">{agent.error.code}</span> — {agent.error.message}
          </div>
        )}
      </div>
    </div>
  );
}


export function ReasoningPreviewPanel() {
  const authFetch = async (url, opts = {}) => fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
    ...opts,
  });
  const [query, setQuery] = useState('');
  const [reasonLoading, setReasonLoading] = useState(false);
  const [reasonResult, setReasonResult] = useState(null);
  const [fanLoading, setFanLoading] = useState(false);
  const [fanRunId, setFanRunId] = useState(null);
  const [fanSnapshot, setFanSnapshot] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(`${API}/ai/v2/knowledge/groups`);
        const data = await r.json();
        setGroups(data.groups || []);
      } catch (e) { /* no-op */ }
    })();
  }, [authFetch]);

  // Poll the fan-out run while in_flight > 0
  useEffect(() => {
    if (!fanRunId) return;
    let stopped = false;
    const poll = async () => {
      while (!stopped) {
        try {
          const r = await authFetch(`${API}/ai/v2/retrieval/runs/${fanRunId}`);
          if (!r.ok) break;
          const snap = await r.json();
          setFanSnapshot(snap);
          if (snap.in_flight === 0) break;
        } catch (e) { break; }
        await new Promise(res => setTimeout(res, 500));
      }
    };
    poll();
    return () => { stopped = true; };
  }, [fanRunId, authFetch]);

  const runReasoning = async () => {
    if (!query.trim()) return;
    setReasonLoading(true);
    setReasonResult(null);
    try {
      const r = await authFetch(`${API}/ai/reason`, {
        method: 'POST',
        body: JSON.stringify({ query: query.trim() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'reason failed');
      setReasonResult(data);
    } catch (e) {
      toast.error(`Reasoning failed: ${e.message || e}`);
    } finally {
      setReasonLoading(false);
    }
  };

  const runFanOut = async () => {
    if (!query.trim()) return;
    setFanLoading(true);
    setFanSnapshot(null);
    setFanRunId(null);
    try {
      const body = selectedGroupId
        ? { query: query.trim(), knowledge_group_id: selectedGroupId, timeout_s: 20 }
        : { query: query.trim(), sources: ['tasks', 'memory', 'eod'], timeout_s: 20 };
      const r = await authFetch(`${API}/ai/v2/retrieval/fan-out`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'fan-out failed');
      setFanRunId(data.run_id);
      setFanSnapshot(data.snapshot);
    } catch (e) {
      toast.error(`Fan-out failed: ${e.message || e}`);
    } finally {
      setFanLoading(false);
    }
  };

  const cancelRun = async (sourceKey = null) => {
    if (!fanRunId) return;
    setCancelling(true);
    try {
      const r = await authFetch(`${API}/ai/v2/retrieval/runs/${fanRunId}/cancel`, {
        method: 'POST',
        body: JSON.stringify(sourceKey ? { source_key: sourceKey } : {}),
      });
      const data = await r.json();
      setFanSnapshot(data.snapshot);
    } catch (e) { /* no-op */ }
    finally { setCancelling(false); }
  };

  return (
    <div className="space-y-4" data-testid="reasoning-preview-panel">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-violet-600" />
            Reasoning Preview
          </CardTitle>
          <CardDescription>
            Paste a query below. "Reason" runs the orchestrator with the current
            system prompt + capability overrides and shows what it would think + which
            tools it would call — <strong>without executing them</strong>. "Fan-out"
            runs the parallel retrieval sub-agents for real against the selected
            knowledge group (or defaults to tasks+memory+eod).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. Find me everything about the Q3 hiring plan."
            rows={3}
            data-testid="reasoning-query-input"
          />
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={selectedGroupId}
              onChange={(e) => setSelectedGroupId(e.target.value)}
              className="text-sm border rounded px-2 py-1.5 bg-background"
              data-testid="reasoning-group-select"
            >
              <option value="">Default (tasks + memory + eod)</option>
              {groups.map(g => (
                <option key={g.group_id} value={g.group_id}>{g.name}</option>
              ))}
            </select>
            <Button onClick={runReasoning} disabled={reasonLoading || !query.trim()}
              data-testid="reasoning-run-btn">
              {reasonLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Brain className="w-4 h-4 mr-1" />}
              Reason
            </Button>
            <Button variant="secondary" onClick={runFanOut} disabled={fanLoading || !query.trim()}
              data-testid="reasoning-fan-out-btn">
              {fanLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}
              Fan out
            </Button>
          </div>
        </CardContent>
      </Card>

      {reasonResult && (
        <Card data-testid="reasoning-result-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Orchestrator reasoning</CardTitle>
            <CardDescription>
              model: <span className="font-mono">{reasonResult.model}</span> · tokens: {reasonResult.usage?.total_tokens || '—'} · latency: {reasonResult.latency_ms}ms · tools visible: {reasonResult.tools_visible}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {reasonResult.thinking && (
              <div className="rounded-md bg-slate-50 dark:bg-slate-900 border p-3 text-sm whitespace-pre-wrap mb-3"
                data-testid="reasoning-thinking-text">
                {reasonResult.thinking}
              </div>
            )}
            <div className="text-xs font-medium orka-text-secondary mb-1">
              Planned tool calls ({reasonResult.planned_count})
            </div>
            {reasonResult.planned_tool_calls.length === 0 ? (
              <div className="text-xs text-slate-500">No tool call — model would respond directly.</div>
            ) : (
              <div className="space-y-2">
                {reasonResult.planned_tool_calls.map((tc, i) => (
                  <div key={i} className="rounded border px-3 py-2 text-xs font-mono bg-slate-50 dark:bg-slate-900"
                    data-testid={`reasoning-plan-call-${i}`}>
                    <div className="font-semibold text-violet-700 dark:text-violet-400">{tc.capability_id}</div>
                    <pre className="mt-1 text-[11px] overflow-x-auto">
{JSON.stringify(tc.arguments, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {fanSnapshot && (
        <Card data-testid="reasoning-fanout-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2">
              <div>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-500" /> Parallel sub-agent run
                </CardTitle>
                <CardDescription>
                  {fanSnapshot.totals.total} sub-agents ·
                  succeeded {fanSnapshot.totals.succeeded} ·
                  failed {fanSnapshot.totals.failed} ·
                  timeout {fanSnapshot.totals.timeout} ·
                  cancelled {fanSnapshot.totals.cancelled} ·
                  skipped {fanSnapshot.totals.skipped} ·
                  items {fanSnapshot.total_items}
                </CardDescription>
              </div>
              {fanSnapshot.in_flight > 0 && (
                <Button variant="destructive" size="sm" onClick={() => cancelRun()}
                  disabled={cancelling} data-testid="reasoning-cancel-all-btn">
                  Stop all
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5" data-testid="reasoning-agents-list">
              {fanSnapshot.agents.map(a => (
                <AgentRow key={a.source} agent={a} />
              ))}
            </div>
            {fanSnapshot.agents.some(a => (a.items || []).length > 0) && (
              <div className="mt-4">
                <div className="text-xs font-medium orka-text-secondary mb-1">Retrieved items</div>
                <ScrollArea className="max-h-[280px]">
                  <div className="space-y-1.5">
                    {fanSnapshot.agents.flatMap(a => (a.items || []).map(item => (
                      <div key={`${a.source}-${item.id}`} className="text-xs rounded border px-2 py-1.5">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px]">{a.source}</Badge>
                          <span className="font-medium truncate">{item.title}</span>
                          {item.url && <a href={item.url} target="_blank" rel="noreferrer" className="text-[10px] text-sky-600 hover:underline">open</a>}
                        </div>
                        {item.snippet && <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{item.snippet}</div>}
                        <div className="text-[10px] text-slate-400 mt-0.5">
                          id {item.id}
                          {item.updated_at ? ` · updated ${item.updated_at}` : ''}
                          {item.owner ? ` · ${item.owner}` : ''}
                        </div>
                      </div>
                    )))}
                  </div>
                </ScrollArea>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default ReasoningPreviewPanel;
